import { useEffect, useRef } from "react";

interface QuizCelebrationProps {
	active: boolean;
}

export function QuizCelebration({ active }: QuizCelebrationProps) {
	const canvasRef = useRef<HTMLCanvasElement>(null);

	useEffect(() => {
		if (!active) return;

		const prefersReduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
		if (prefersReduced) return;

		const canvas = canvasRef.current;
		if (!canvas) return;

		const ctx = canvas.getContext("2d");
		if (!ctx) return;

		canvas.width = window.innerWidth;
		canvas.height = window.innerHeight;

		const colors = ["#EA580C", "#F97316", "#FBBF24", "#22C55E", "#3B82F6"];

		interface Particle {
			x: number;
			y: number;
			vx: number;
			vy: number;
			size: number;
			color: string;
			rotation: number;
			rotationSpeed: number;
			life: number;
		}

		const particles: Particle[] = [];

		for (let i = 0; i < 80; i++) {
			particles.push({
				x: canvas.width / 2 + (Math.random() - 0.5) * 200,
				y: canvas.height / 2,
				vx: (Math.random() - 0.5) * 12,
				vy: Math.random() * -14 - 4,
				size: Math.random() * 8 + 4,
				color: colors[Math.floor(Math.random() * colors.length)] as string,
				rotation: Math.random() * Math.PI * 2,
				rotationSpeed: (Math.random() - 0.5) * 0.3,
				life: 1,
			});
		}

		let animFrame: number;
		const animate = () => {
			ctx.clearRect(0, 0, canvas.width, canvas.height);

			let alive = false;
			for (const p of particles) {
				if (p.life <= 0) continue;
				alive = true;

				p.x += p.vx;
				p.y += p.vy;
				p.vy += 0.3;
				p.rotation += p.rotationSpeed;
				p.life -= 0.012;

				ctx.save();
				ctx.globalAlpha = Math.max(0, p.life);
				ctx.translate(p.x, p.y);
				ctx.rotate(p.rotation);
				ctx.fillStyle = p.color;
				ctx.fillRect(-p.size / 2, -p.size / 2, p.size, p.size * 0.6);
				ctx.restore();
			}

			if (alive) {
				animFrame = requestAnimationFrame(animate);
			}
		};

		animFrame = requestAnimationFrame(animate);

		return () => {
			cancelAnimationFrame(animFrame);
		};
	}, [active]);

	if (!active) return null;

	return (
		<canvas
			ref={canvasRef}
			className="pointer-events-none fixed inset-0 z-toast"
			tabIndex={-1}
			aria-hidden="true"
		/>
	);
}
